import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoGrid, ReportFooterTimestamp } from '../../../utils/reportUtils';
import { LaparoscopyType } from './laparoscopy.helper';

interface LaparoscopyReportProps {
  data: LaparoscopyType;
}

const LaparoscopyReport: React.FC<LaparoscopyReportProps> = ({ data }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');

  const hasValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== '';
  };

  const buildGridData = (fields: Array<[string, string | number | null | undefined]>): string[][] => {
    const filtered = fields
      .filter(([, value]) => hasValue(value))
      .map(([label, value]) => [label, String(value)] as [string, string]);

    const rows: string[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      const left = filtered[i];
      const right = filtered[i + 1];
      rows.push([
        left?.[0] ?? '',
        left?.[1] ?? '',
        right?.[0] ?? '',
        right?.[1] ?? '',
      ]);
    }
    return rows;
  };

  const procedureInfo = buildGridData([
    [tGeneral('date', { defaultValue: 'Date' }), data.date],
    [tSurgery('indication', { defaultValue: 'Indication' }), data.indication_label],
    [tSurgery('cycleDay', { defaultValue: 'Cycle Day' }), data.cycle_day],
    [tSurgery('procedureDuration', { defaultValue: 'Procedure Duration' }), data.procedure_duration],
  ]);

  const anesthesiaInfo = buildGridData([
    [tSurgery('anesthesiaType', { defaultValue: 'Anesthesia Type' }), data.anesthesia_type_label],
    [tSurgery('anesthesiaDetailsRemark', { defaultValue: 'Anesthesia Remark' }), data.anesthesia_details_remark],
    [tSurgery('preanestheticCheckup', { defaultValue: 'Pre-anesthetic Checkup' }), data.preanesthetic_checkup],
  ]);

  const patencyInfo = buildGridData([
    [tSurgery('rightTube', { defaultValue: 'Right Tube' }), data.right_tube_status],
    [tSurgery('leftTube', { defaultValue: 'Left Tube' }), data.left_tube_status],
  ]);

  const rightOvaryInfo = buildGridData([
    [tSurgery('rightOvary', { defaultValue: 'Right Ovary' }), data.right_ovary_status],
    [tSurgery('details', { defaultValue: 'Details' }), data.right_ovary_details],
  ]);

  const leftOvaryInfo = buildGridData([
    [tSurgery('leftOvary', { defaultValue: 'Left Ovary' }), data.left_ovary_status],
    [tSurgery('otherDetails', { defaultValue: 'Other Details' }), data.left_ovary_details],
  ]);

  const otherDetailsInfo = buildGridData([
    [tSurgery('site', { defaultValue: 'Site' }), data.adhesion_site_label],
    [tSurgery('adhesion', { defaultValue: 'Adhesion' }), data.adhesion_grade_label],
    [tGeneral('remark', { defaultValue: 'Remark' }), data.remark],
    [tSurgery('endometriosisGrade', { defaultValue: 'Endometriosis Grade' }), data.endometriosis_grade_label],
    [tSurgery('uterusType', { defaultValue: 'Uterus Type' }), data.uterus_status_label],
    [tSurgery('size', { defaultValue: 'Size' }), data.uterus_size_label],
    [tSurgery('shape', { defaultValue: 'Shape' }), data.uterus_shape_label],
    [tSurgery('position', { defaultValue: 'Position' }), data.uterus_position_label],
    [tSurgery('mobility', { defaultValue: 'Mobility' }), data.uterus_mobility_label],
    [tSurgery('associatedProcedure', { defaultValue: 'Associated Procedure' }), data.associated_procedures_label],
    [tGeneral('remark', { defaultValue: 'Remark' }), data.eb_hpe_remark],
    [tSurgery('doneBy', { defaultValue: 'Done By' }), data.done_by_name],
    [tSurgery('associatedDoctor', { defaultValue: 'Associated Doctor' }), data.associated_doctor_name],
  ]);

  const renderSection = (title: string, rows: string[][]) => {
    if (rows.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        <InfoGrid data={rows} />
      </Box>
    );
  };

  return (
    <Box sx={{ p: '8px 35px 35px 35px', backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif' }}>
        
      {renderSection(tSurgery('clinicalAndProcedureInfo', { defaultValue: 'Clinical and Procedure Info' }), procedureInfo)}
      {renderSection(tSurgery('anesthesiaDetails', { defaultValue: 'Anesthesia Details' }), anesthesiaInfo)}
      {renderSection(tSurgery('patencyOfFallopianTube', { defaultValue: 'Patency Of Fallopian Tube' }), patencyInfo)}
      {renderSection(tSurgery('rightOvary', { defaultValue: 'Right Ovary' }), rightOvaryInfo)}
      {renderSection(tSurgery('leftOvary', { defaultValue: 'Left Ovary' }), leftOvaryInfo)}
      {renderSection(tSurgery('otherDetailsSection', { defaultValue: 'Other Details' }), otherDetailsInfo)}

      <ReportFooterTimestamp />
    </Box>
  );
};

export default LaparoscopyReport;